import * as React from 'react';

import { Box, Typography, Button } from '@mui/material';
import {
    KeycloakHasRole,
    KeycloakIsAuth,
    REALM_DEBUG,
    UseKeycloakService,
} from '@europrocurement/l2d-keycloak';
import { Link } from 'react-router-dom';
import {
    FaOptionIcon,
    NamedIconsType,
    closeIcon,
    codeIcon,
    companyIcon,
    mailIcon,
    preferencesIcon,
    quitIcon,
    userIcon,
    usersIcon,
} from '@europrocurement/l2d-icons';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';
import { FlexyIconButton, TextIcon } from '../../atoms';

export type EntryType = {
    label: string;
    icon: NamedIconsType;
    actionButton: () => void;
};
export type ProfileDropdownProps = {
    jobName?: string;
    logout: () => void;
    onClose: () => void;
    navigateToPrescripteur?: () => void;
    navigateToMembers?: () => void;
    openSettings?: (targetKey: string) => void;
    otherEntries?: EntryType[];
    manageAvatar?: React.ReactNode;
};
export const ProfileDropdown: React.FunctionComponent<ProfileDropdownProps> = function ({
    jobName,
    logout,
    onClose,
    navigateToPrescripteur,
    navigateToMembers,
    openSettings,
    otherEntries,
    manageAvatar,
}: ProfileDropdownProps) {
    const keycloak = UseKeycloakService();
    const { palette } = useL2DTheme();

    return (
        <>
            <Box
                sx={{
                    padding: '30px 30px 8px 30px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        verticalAlign: 'middle',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="l2dh6">Mon compte</Typography>

                    <FlexyIconButton
                        onClick={onClose}
                        icon={closeIcon}
                        tooltipOverwriteProps={{ title: closeIcon.displayName }}
                        normalHoverColor={palette.text.secondary}
                        buttonOverwriteProps={{
                            sx: {
                                minWidth: '0',
                                padding: '0',
                                fontSize: 'inherit',
                                lineHeight: '0',
                                paddingBottom: '5px',
                            },
                        }}
                    />
                </Box>
                <Box
                    role="menu"
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        paddingTop: '16px',
                        paddingBottom: '25px',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                    }}
                >
                    {/* <CustomAvatar
                        firstName={keycloak.getName().split(' ')[0]}
                        lastName={keycloak.getName().split(' ')[1]}
                        src={avatarSrc}
                        sx={{ width: '90px', height: '90px' }}
                    /> */}
                    {manageAvatar}

                    <Box
                        sx={{
                            marginLeft: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {keycloak.getName() ? (
                            <Typography
                                lineHeight="1.4"
                                variant="l2dh6"
                                sx={{
                                    fontSize: '1.125rem',
                                }}
                            >
                                {keycloak.getName()}
                            </Typography>
                        ) : null}
                        {jobName ? (
                            <Typography
                                color="text.gris50"
                                variant="l2dcaption"
                                lineHeight="1.4"
                            >
                                {jobName}
                            </Typography>
                        ) : null}
                        {keycloak.getEmail() ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: 'text.gris50',
                                }}
                            >
                                <FaOptionIcon
                                    icon={mailIcon.props.icon}
                                    size="xs"
                                />
                                <Typography
                                    lineHeight="1.4"
                                    color="text.gris50"
                                    variant="l2dcaption"
                                >
                                    {keycloak.getEmail()}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    padding: '0px 30px 30px 30px',
                }}
            >
                <Typography
                    variant="l2dcaption"
                    color="text.gris50"
                >
                    Mon profil
                </Typography>
                {openSettings ? (
                    <>
                        <Box sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
                            <Button
                                variant="text"
                                onClick={() => {
                                    openSettings('account_details');
                                }}
                                sx={{ color: 'text.gris70', alignItems: 'normal', fontWeight: 500 }}
                            >
                                <TextIcon icon={userIcon}>Mon compte</TextIcon>
                            </Button>
                        </Box>
                        <Box sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
                            <Button
                                variant="text"
                                onClick={() => {
                                    openSettings('themes');
                                }}
                                sx={{ color: 'text.gris70', alignItems: 'normal', fontWeight: 500 }}
                            >
                                <TextIcon icon={preferencesIcon}>Mes préférences</TextIcon>
                            </Button>
                        </Box>
                    </>
                ) : null}
                {navigateToPrescripteur || navigateToMembers ? (
                    <Typography
                        variant="l2dcaption"
                        color="text.gris50"
                    >
                        Paramètres de la société
                    </Typography>
                ) : null}
                {navigateToPrescripteur ? (
                    <Box sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
                        <Button
                            onClick={navigateToPrescripteur}
                            variant="text"
                            sx={{ color: 'text.gris70', alignItems: 'normal', fontWeight: 500 }}
                        >
                            <TextIcon icon={companyIcon}>Ma structure</TextIcon>
                        </Button>
                    </Box>
                ) : null}
                {navigateToMembers ? (
                    <Box sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
                        <Button
                            onClick={navigateToMembers}
                            variant="text"
                            sx={{ color: 'text.gris70', alignItems: 'normal', fontWeight: 500 }}
                        >
                            <TextIcon icon={usersIcon}>Membres</TextIcon>
                        </Button>
                    </Box>
                ) : null}
                {otherEntries && otherEntries.length > 0
                    ? otherEntries.map((entry) => (
                          <Box
                              key={`${entry.label.replaceAll(' ', '')}`}
                              sx={{ paddingTop: '4px', paddingBottom: '4px' }}
                          >
                              <Button
                                  onClick={entry.actionButton}
                                  variant="text"
                                  sx={{
                                      color: 'text.gris70',
                                      alignItems: 'normal',
                                      fontWeight: 500,
                                  }}
                              >
                                  <TextIcon icon={entry.icon}> {entry.label} </TextIcon>
                              </Button>
                          </Box>
                      ))
                    : null}
                <KeycloakIsAuth>
                    <>
                        <KeycloakHasRole kcRole={REALM_DEBUG}>
                            <Typography
                                variant="l2dcaption"
                                color="text.gris50"
                            >
                                Administration
                            </Typography>

                            <Box sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
                                <Button
                                    color="primary"
                                    variant="text"
                                    component={Link}
                                    to="/debug"
                                    disableElevation
                                    sx={{
                                        color: 'text.gris70',
                                        alignItems: 'normal',
                                        fontWeight: 500,
                                    }}
                                >
                                    <TextIcon icon={codeIcon}>Outils de développement</TextIcon>
                                </Button>
                            </Box>
                        </KeycloakHasRole>
                        <Box
                            sx={{
                                borderTop: '1px solid',
                                borderTopColor: 'divider',
                                paddingTop: '8px',
                            }}
                        >
                            <Button
                                onClick={logout}
                                variant="text"
                                title="Déconnexion"
                                sx={{ color: 'text.gris70', alignItems: 'normal', fontWeight: 500 }}
                            >
                                <TextIcon icon={quitIcon}>Se déconnecter</TextIcon>
                            </Button>
                        </Box>
                    </>
                </KeycloakIsAuth>
            </Box>
        </>
    );
};
